import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Check,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  Date helpers                                                              */
/* -------------------------------------------------------------------------- */

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// Local YYYY-MM-DD (no UTC shift — backend parses this as a Sydney date)
const toYMD = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseYMD = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const isSameDay = (a, b) =>
  !!a &&
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const fmtLong = (d) =>
  d
    ? d.toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";

// Build the 6-week grid (leading/trailing days from neighbouring months)
const buildCalendarDays = (year, month) => {
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) {
    cells.push({
      date: new Date(year, month, i - firstWeekday + 1),
      current: false,
    });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month, d), current: true });
  }
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1].date;
    cells.push({
      date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1),
      current: false,
    });
  }
  return cells;
};

// Last N months as { year, month } starting from the current month
const buildMonthOptions = (count = 24) => {
  const now = new Date();
  const opts = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({ year: d.getFullYear(), month: d.getMonth() });
  }
  return opts;
};

/* -------------------------------------------------------------------------- */
/*  DatePicker — Month tab + Date Range calendar                              */
/*                                                                            */
/*  Props:                                                                    */
/*    value:    { startDate: "YYYY-MM-DD" | "", endDate: "YYYY-MM-DD" | "" }   */
/*    onChange: ({ startDate, endDate }) => void                              */
/*                                                                            */
/*  A month selection resolves to first-day -> last-day of that month, so the  */
/*  backend's date filter handles both cases with a single startDate/endDate.  */
/* -------------------------------------------------------------------------- */

const DatePicker = ({ value = {}, onChange }) => {
  const { startDate = "", endDate = "" } = value;
  const ref = useRef(null);

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("month");

  // working state for the calendar (committed only on Apply)
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [viewDate, setViewDate] = useState(new Date());

  const monthOptions = useMemo(() => buildMonthOptions(24), []);
  const isAllTime = !startDate && !endDate;

  // A committed selection counts as a "month" only if it spans an exact calendar month
  const selectedMonth = useMemo(() => {
    const s = parseYMD(startDate);
    const e = parseYMD(endDate);
    if (!s || !e) return null;
    const lastOfMonth = new Date(s.getFullYear(), s.getMonth() + 1, 0);
    if (s.getDate() === 1 && isSameDay(e, lastOfMonth)) {
      return { year: s.getFullYear(), month: s.getMonth() };
    }
    return null;
  }, [startDate, endDate]);

  const label = useMemo(() => {
    if (isAllTime) return "All Time";
    if (selectedMonth)
      return `${MONTHS[selectedMonth.month]} ${selectedMonth.year}`;
    return `${fmtLong(parseYMD(startDate))} – ${fmtLong(parseYMD(endDate))}`;
  }, [startDate, endDate, isAllTime, selectedMonth]);

  // close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // sync working state whenever the dropdown opens
  useEffect(() => {
    if (!open) return;
    const s = parseYMD(startDate);
    const e = parseYMD(endDate);
    setRangeStart(s);
    setRangeEnd(e);
    setViewDate(s || new Date());
    setTab(s && e && !selectedMonth ? "range" : "month");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- month tab ---- */
  const selectAllTime = () => {
    onChange?.({ startDate: "", endDate: "" });
    setOpen(false);
  };
  const selectMonth = (year, month) => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    onChange?.({ startDate: toYMD(first), endDate: toYMD(last) });
    setOpen(false);
  };

  /* ---- range tab ---- */
  const handleDayClick = (date) => {
    const d = startOfDay(date);
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(d);
      setRangeEnd(null);
    } else if (d < rangeStart) {
      setRangeStart(d);
      setRangeEnd(null);
    } else {
      setRangeEnd(d);
    }
  };
  const applyRange = () => {
    if (rangeStart && rangeEnd) {
      onChange?.({ startDate: toYMD(rangeStart), endDate: toYMD(rangeEnd) });
      setOpen(false);
    }
  };
  const clearRange = () => {
    setRangeStart(null);
    setRangeEnd(null);
    onChange?.({ startDate: "", endDate: "" });
  };

  const inRange = (d) => {
    if (!rangeStart) return false;
    const end = rangeEnd || rangeStart;
    const lo = rangeStart < end ? rangeStart : end;
    const hi = rangeStart < end ? end : rangeStart;
    return d >= lo && d <= hi;
  };

  const cells = buildCalendarDays(viewDate.getFullYear(), viewDate.getMonth());

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="form-input border border-mediumgray w-full flex items-center justify-between text-left"
      >
        <span className="truncate text-black">{label}</span>

        <ChevronDown
          className={`w-4 h-4 text-darkgray flex-shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Panel */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[340px] bg-white rounded-xl border border-mediumgray shadow-xl p-3"
          style={{ zIndex: 99999 }}
        >
          {/* Tabs */}
          <div className="flex border-b border-mediumgray mb-3">
            {[
              ["month", "Month"],
              ["range", "Date Range"],
            ].map(([key, text]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  tab === key
                    ? "text-black border-b-2 border-black"
                    : "text-darkgray hover:text-black"
                }`}
              >
                {text}
              </button>
            ))}
          </div>

          {/* Month tab */}
          {tab === "month" && (
            <div className="max-h-72 overflow-y-auto">
              <button
                type="button"
                onClick={selectAllTime}
                className="w-full flex items-center justify-between px-2 py-2.5 hover:bg-lightgray rounded-lg"
              >
                <span
                  className={`text-sm ${
                    isAllTime ? "font-bold text-black" : "text-black"
                  }`}
                >
                  All Time
                </span>
                {isAllTime && <Check className="w-4 h-4 text-black" />}
              </button>

              {monthOptions.map(({ year, month }) => {
                const selected =
                  selectedMonth &&
                  selectedMonth.year === year &&
                  selectedMonth.month === month;
                return (
                  <button
                    key={`${year}-${month}`}
                    type="button"
                    onClick={() => selectMonth(year, month)}
                    className="w-full flex items-center justify-between px-2 py-2.5 hover:bg-lightgray rounded-lg"
                  >
                    <span
                      className={`text-sm ${
                        selected ? "font-bold text-black" : "text-black"
                      }`}
                    >
                      {MONTHS[month]} {year}
                    </span>
                    {selected && <Check className="w-4 h-4 text-black" />}
                  </button>
                );
              })}
            </div>
          )}

          {/* Date Range tab */}
          {tab === "range" && (
            <div>
              {/* Calendar header */}
              <div className="flex items-center justify-between mb-2 px-1">
                <button
                  type="button"
                  onClick={() =>
                    setViewDate(
                      new Date(
                        viewDate.getFullYear(),
                        viewDate.getMonth() - 1,
                        1,
                      ),
                    )
                  }
                  className="p-1.5 hover:bg-lightgray rounded-lg"
                >
                  <ChevronLeft className="w-4 h-4 text-darkgray" />
                </button>
                <p className="font-semibold text-black">
                  {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
                </p>
                <button
                  type="button"
                  onClick={() =>
                    setViewDate(
                      new Date(
                        viewDate.getFullYear(),
                        viewDate.getMonth() + 1,
                        1,
                      ),
                    )
                  }
                  className="p-1.5 hover:bg-lightgray rounded-lg"
                >
                  <ChevronRight className="w-4 h-4 text-darkgray" />
                </button>
              </div>

              {/* Weekday header */}
              <div className="grid grid-cols-7">
                {WEEKDAYS.map((w) => (
                  <div
                    key={w}
                    className="h-8 flex items-center justify-center text-xs text-darkgray"
                  >
                    {w}
                  </div>
                ))}
              </div>

              {/* Day grid */}
              <div className="grid grid-cols-7 gap-y-1">
                {cells.map(({ date, current }, i) => {
                  const isStart = isSameDay(date, rangeStart);
                  const isEnd = rangeEnd && isSameDay(date, rangeEnd);
                  const endpoint = isStart || isEnd;
                  const within = inRange(date);

                  let cls =
                    "h-9 w-full text-sm flex items-center justify-center transition-colors";
                  if (endpoint) {
                    cls += " bg-black text-white font-semibold rounded-lg";
                  } else if (within) {
                    cls += " bg-lightgray text-black";
                  } else {
                    cls += ` rounded-lg ${
                      current
                        ? "text-black hover:bg-lightgray"
                        : "text-mediumgray hover:bg-lightgray"
                    }`;
                  }

                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleDayClick(date)}
                      className={cls}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between border-t border-mediumgray pt-3 mt-3">
                <span className="text-sm text-darkgray">
                  {rangeStart && rangeEnd
                    ? `${fmtLong(rangeStart)} – ${fmtLong(rangeEnd)}`
                    : rangeStart
                      ? `${fmtLong(rangeStart)} – …`
                      : "Select a date range"}
                </span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={clearRange}
                    className="text-sm text-darkgray hover:text-black"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={applyRange}
                    disabled={!rangeStart || !rangeEnd}
                    className="bg-black text-white text-sm px-4 py-1.5 rounded-lg hover:bg-darkgray disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DatePicker;
